import type { CSSProperties } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { CardFab } from '@features/CreateCard';
import { ModuleCreatorModal } from '@features/CreateModule';
import { NotificationsListener } from '@features/Notifications';
import { SocialRewardProvider } from '@features/SocialReward';
import { ToastContainer } from '@shared/ui';
import { MobileNav, Sidebar, TAB_BAR_HEIGHT, useIsSidebarMobile, useSidebarStore, useSoftKeyboardOpen } from '@widgets/Sidebar';

export default function Application() {
  // Главная: «Встретилось новое слово?» ведёт туда же — кнопка дублировала бы плашку (владелец 2026-10-23).
  const onHome = useMatch('/');
  // «Слова и модули»: карточки создаются строкой внизу — плавающая кнопка закрывала бы её.
  const onWords = useMatch('/words');
  const onUnit = useMatch('/units/:id');
  const onCram = useMatch('/units/:id/cram');
  // Шаг 1 тренировки (выбор модуля) — тоже без «+ Карточка» (2026-10-07), как и конструктор.
  const onTraining = useMatch('/training');
  const onUnitCardsNew = useMatch('/units/:id/cards/new');
  const onCardsNew = useMatch('/cards/new');
  const onTopicCards = useMatch('/cards/topics');
  const onPlans = useMatch('/plans');
  // «Полезное»: тренажёры и таблицы — кнопка поверх закрывала бы ответы и «Показать ещё».
  const onUseful = useMatch('/useful/*');
  // Урок грамматики: внизу липкая «К упражнению» — кнопка поверх закрывала бы её.
  const onGrammar = useMatch('/grammar/*');
  // Настройки — не место создания карточек: на 320 px кнопка закрывала подписи, поля и переключатели (приёмка A1-16).
  const onProfile = useMatch('/profile');
  // «Выживи в Лондоне»: кнопка закрывала бы карточки игр и карту.
  const onStories = useMatch('/stories/*');
  // «Задание дня»: внизу липкая «Учить слова» — кнопка поверх закрывала бы её на телефоне.
  const onDailyTask = useMatch('/daily');

  const collapsed = useSidebarStore((s) => s.collapsed);
  const isMobile = useIsSidebarMobile();
  // Телефон: панель вкладок — нижняя строка сетки; с открытой клавиатурой её нет (useSoftKeyboardOpen).
  const typing = useSoftKeyboardOpen();
  const tabBar = isMobile && !typing;
  const shell = {
    gridTemplateColumns: isMobile ? '1fr' : `${collapsed ? 72 : 240}px 1fr`,
    gridTemplateRows: isMobile ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
    // Высота панели — для слоёв, что встают над ней: «+ Карточка», лист «Ещё».
    '--tabbar-h': `${tabBar ? TAB_BAR_HEIGHT : 0}px`,
  } as CSSProperties;
  // «+ Карточка» ведёт на «Слова и модули» с фокусом в строке создания (2026-10-23; до этого строка жила на главной).
  const showFab =
    !onHome &&
    !onWords &&
    !onUnit &&
    !onCram &&
    !onTraining &&
    !onUnitCardsNew &&
    !onCardsNew &&
    !onTopicCards &&
    !onPlans &&
    !onUseful &&
    !onGrammar &&
    !onProfile &&
    !onStories &&
    !onDailyTask;

  return (
    // Подарок за подписку — один контроллер и один диалог на приложение: строка в боковой панели, лист «Ещё»,
    // «Сообщество» и строка создания открывают его (ТЗ главной § 11).
    <SocialRewardProvider>
      {/* .app-shell — высота видимой области (100dvh, index.css): с 100vh низ main на телефоне
          уходил под панель браузера. main — контейнер прокрутки; у предков липкой строки создания
          на «Словах и модулях» не должно быть transform, filter, opacity < 1, contain и прочего (ТЗ главной § 4). */}
      <div className="app-shell grid w-screen transition-[grid-template-columns] duration-300" style={shell}>
        {!isMobile && <Sidebar />}
        <main className="relative min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
          <Outlet />
        </main>
        {isMobile && <MobileNav tabBar={tabBar} />}
        <ModuleCreatorModal />
        {showFab && <CardFab />}
        <ToastContainer />
        <NotificationsListener />
      </div>
    </SocialRewardProvider>
  );
}
